import React, { useMemo, useCallback, useEffect, useState, useContext, useRef } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import DDSideNavbar from '../8_diagnosticDecision/components/DDSideNavbar';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import {
  ADHD_CONDITION_LABEL,
  buildDDNavSections,
  getDDRouteForSectionIndex,
  hasActionableOtherConditions,
  isAdhdPositiveDecision,
} from './cypDdRouting';
import { isClinicianAssignedToStage } from '../../../utils/clinicianPermissions';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import CypAdhdSupportingDocumentsPanel from '../../../components/CypAdhdSupportingDocumentsPanel';
import { formatDateTime, isValidTimestamp } from '../../../utils/timestampUtils';
import { auth } from '../../../firebase';
import CypSnapScalesPanel, { snapResultsFromInformantView } from '../5_patientOverview/cypAdhd/CypSnapScalesPanel';
import FormDetailsPanel from '../5_patientOverview/components/FormDetailsPanel';
import { buildFormDataMapFromInstances } from '../5_patientOverview/utils/buildModularPatientOverviewModel';
import { resolveFormInstanceForDisplay } from '../../../utils/resolveFormTemplates';
import CypAdhdBoaCapturePanel from './components/CypAdhdBoaCapturePanel';
import { fetchEnrichedInformantData } from '../../../utils/informantFirestorePaths';
import ConditionSelectionPanelTOC from '../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import useEmergingFormulations from '../../../hooks/useEmergingFormulations';
import CypAdhdSsiDecisionTables from '../22_cypAdhdSSI/components/CypAdhdSsiDecisionTables';
import CypAdhdDecisionSummaryDialog from '../22_cypAdhdSSI/components/CypAdhdDecisionSummaryDialog';
import {
  buildCypSsiDecisionRows,
  cypSsiSummaryColumns,
} from '../22_cypAdhdSSI/config/cypAdhdSsiConfig';
import DualClinicianEmergingNotes from '../8_diagnosticDecision/components/DualClinicianEmergingNotes';
import CypAdhdMedicalScreeningSummary from '../19_cypAdhdCI/components/CypAdhdMedicalScreeningSummary';
import {
  CYP_MEDICAL_SCREENING_GROUPS,
  CYP_MEDICAL_SCREENING_SOURCE_NOTE,
} from '../19_cypAdhdCI/config/cypAdhdMedicalScreeningConfig';

/**
 * DD Review step for multi-step Diagnostic Decision workflows (TOC, SLaM).
 *
 * Sits BEFORE the Decision for ADHD page. Read-only consolidation of assessment
 * information so the Lead (or MDT) can review everything before deciding.
 *
 * Same order as the adult ADHD MDT Review:
 *  1. Supporting Documents (approved CYP Clinical Interview and
 *     Semi-Structured Interview PDFs), then the SNAP-IV rating scales
 *  2. Condition(s) Referred For + Emerging Differentials
 *  3. SSI symptom and impairment decisions, in the same pop-up tables as
 *     the CYP Review & Synthesis step
 *  4. Emerging Formulation + Confidential Notes
 *  Page title: "MDT Review" for MDT orgs (WLC, TOC); "Review" otherwise.
 *
 * Patients without multi-step DD are redirected to /adhd.
 *
 * Firestore writes: the page content is read-only, but two side effects can
 * write — the one-shot notStarted -> inProgress status promotion (below) and
 * the BOA capture panel for orgs that record the Behavioural Observation
 * Appointment here. Both are suppressed in preview mode.
 *
 * Preview mode (?preview=true, opened from the overview's Preview button):
 * usePreviewAwareNavigate keeps the flag on every step hop so the viewer
 * stays read-only across the whole DD wizard, PreviewModeBanner tells them
 * so, and useCanEditStage({ previewMode }) forces canEdit=false even for the
 * assigned DD clinician — identical to the Adult Autism DD wizard.
 */

export default function DDReview() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();
  const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
  const { assessmentStages, reportContent } = useContext(ModularPatientDataContext);
  const { claims } = useAuth();
  // CYP DD is unconditionally multi-step (the 4-stage wizard ending at Final
  // Formulation). Override the adult hasMultiStepDD — which reads from the
  // immutable ddFlow stamp and may be false on older patients — with the
  // CYP-specific cypHasMultiStepDD so every CYP patient gets the wizard.
  const { cypHasMultiStepDD, ...baseWf } = useWorkflowConfig(patientDataModel, claims);
  const wf = { ...baseWf, hasMultiStepDD: cypHasMultiStepDD };
  // Drives the BOA capture panel: the assigned DD clinician may record the
  // BOA outside preview; everyone else (and anyone previewing) only reads it.
  const canEdit = useCanEditStage(patientDataModel, 'DD', { previewMode: isPreviewMode });

  // ---------------------------------------------------------------------------
  // Route guard: redirect patients without multi-step DD away from this page
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (patientDataModel && !wf.hasMultiStepDD) {
      navigate(
        `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/adhd`,
        { replace: true }
      );
    }
  }, [patientDataModel, wf.hasMultiStepDD, navigate, patientId]);

  // ---------------------------------------------------------------------------
  // "Entered" marker for the DD module (multi-step orgs: TOC, SLaM, 3 Borough)
  // ---------------------------------------------------------------------------
  // Review is the canonical entry point for multi-step DD. Visiting it means the
  // clinician has opened the module, so the Patient Overview card should show
  // partial progress (25%) and a "Continue" button that resumes at the ADHD
  // decision. We record entry by promoting the stage status
  // notStarted -> inProgress (same pattern as the Adult Autism DD module). This
  // is a one-shot, gated no-op for read-only viewers, previewers, non-multi-step
  // orgs, and any stage that is already locked/inProgress/completed.
  //
  // Preview guard: "Preview" means "look, do not touch". Promoting the status
  // would flip the overview card from Start to Continue for a module nobody
  // has actually begun, so the assigned clinician previewing their own module
  // must not trigger it either.
  const hasPromotedDDStatusRef = useRef(false);
  useEffect(() => {
    if (hasPromotedDDStatusRef.current) return;
    if (isPreviewMode) return;
    if (!patientDataModel || !wf.hasMultiStepDD) return;
    const uid = auth.currentUser?.uid;
    if (!uid || !isClinicianAssignedToStage(patientDataModel, uid, 'DD')) return;
    const ddStatus = patientDataModel?.dataModel?.['4_diagnosticDecision']?.status;
    if (ddStatus !== 'notStarted') return;
    hasPromotedDDStatusRef.current = true;
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': { status: 'inProgress' },
    });
  }, [patientDataModel, wf.hasMultiStepDD, patientId, updatePatientFirestore, isPreviewMode]);

  // ---------------------------------------------------------------------------
  // Informant data — fetched so PatientScalesPanel can display informant
  // rating scales without getting stuck on a permanent "Loading..." spinner.
  // Mirrors the fetch logic in PatientOverview.js exactly, including the
  // isCurrent guard to prevent race conditions on fast patient switches.
  // ---------------------------------------------------------------------------
  const [informantData, setInformantData] = useState({});

  useEffect(() => {
    let isCurrent = true;
    setInformantData({});

    const fetchInformantData = async () => {
      if (!patientDataModel?.informants || Object.keys(patientDataModel.informants).length === 0) {
        return;
      }

      const nextInformantsData = {};
      // CYP informants live in informants/{uid} subcollection trees, not in
      // patients/ — the enrichment fetch also carries formInstances, which
      // the SNAP panel reads for scaleResults.
      const subcollectionAccess = {
        organisation: patientDataModel?.organisation || claims?.organisation || '',
        clinicianUid: auth.currentUser?.uid || '',
        isOrgWideAccess: claims?.userType === 'Org. Admin' || claims?.userType === 'Support',
      };
      for (const [informantId, mapEntry] of Object.entries(patientDataModel.informants)) {
        try {
          const enriched = await fetchEnrichedInformantData(
            informantId, patientId, mapEntry, subcollectionAccess,
          );
          if (!isCurrent) return;
          if (enriched) {
            // Same preparation the Patient Overview does before the
            // magnifying glass can open a readable SNAP-IV. A CYP informant
            // stores answers as form instances, and the detail panel reads
            // formData, so the instances are turned into that shape here.
            // Names in the form titles ("About {{patientFirstName}}") are
            // filled in at the same time, or the panel would show the token.
            const patientFirstName = patientDataModel?.firstName || '';
            const resolvedFormInstances = enriched.isAutismInformant
              ? Object.fromEntries(
                Object.entries(enriched.formInstances || {}).map(([key, fi]) => [
                  key,
                  resolveFormInstanceForDisplay(fi, patientFirstName),
                ]),
              )
              : enriched.formInstances;
            nextInformantsData[informantId] = {
              ...enriched,
              formInstances: resolvedFormInstances,
              formData: enriched.isAutismInformant
                ? buildFormDataMapFromInstances(resolvedFormInstances)
                : enriched.formData,
            };
          }
        } catch (error) {
          console.error(`Error fetching informant data for ${informantId}:`, error);
        }
      }

      if (isCurrent) {
        setInformantData(nextInformantsData);
      }
    };

    fetchInformantData();

    return () => { isCurrent = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, patientDataModel?.informants]);

  // The SNAP panel takes per-role views (parent/carer + school). The id
  // travels with the view so the magnifying glass can open that
  // informant's raw SNAP-IV answers, the same drill-down as Patient Overview.
  const guardianEntry = Object.entries(patientDataModel?.informants || {})
    .filter(([, m]) => m?.informantRole === 'legalGuardian')
    .map(([id]) => [id, informantData[id]])
    .find(([, view]) => view) || null;
  const schoolRepEntry = Object.entries(patientDataModel?.informants || {})
    .filter(([, m]) => m?.informantRole === 'schoolRep')
    .map(([id]) => [id, informantData[id]])
    .find(([, view]) => view) || null;
  const guardianId = guardianEntry?.[0] || null;
  const guardianView = guardianEntry?.[1] || null;
  const schoolRepId = schoolRepEntry?.[0] || null;
  const schoolRepView = schoolRepEntry?.[1] || null;

  // One detail panel, shared by both magnifying glasses. A glass is only
  // offered once that informant has a completed SNAP-IV — an empty row
  // would open an empty panel.
  const [scaleDetailForm, setScaleDetailForm] = useState(null);
  const [isScaleDetailOpen, setIsScaleDetailOpen] = useState(false);
  const openScaleDetail = useCallback((informantId) => {
    setScaleDetailForm({
      key: 'snapIV26Informant',
      source: 'informant',
      informantId,
    });
    setIsScaleDetailOpen(true);
  }, []);
  const handleViewGuardianScale = guardianId && snapResultsFromInformantView(guardianView)
    ? () => openScaleDetail(guardianId)
    : undefined;
  const handleViewSchoolScale = schoolRepId && snapResultsFromInformantView(schoolRepView)
    ? () => openScaleDetail(schoolRepId)
    : undefined;

  // ---------------------------------------------------------------------------
  // Emerging data — TOC uses single-source CI12/SSI3 (via hook fallback);
  // SLaM uses emergingFormulations for both SSI + Lead clinicians.
  // ---------------------------------------------------------------------------
  const emerging = useEmergingFormulations(patientDataModel);

  // TOC Emerging Differentials (CI section 12). ADHD shown separately above.
  // For dual-clinician SLaM, prefer the SSI clinician's differentials list
  // (what was recorded at SSI Review); fall back to Lead if SSI has none.
  const differentials = useMemo(() => {
    const source = emerging.isDualClinician
      ? (emerging.ssiClinician?.differentials?.length
          ? emerging.ssiClinician.differentials
          : (emerging.leadClinician?.differentials || []))
      : (emerging.leadClinician?.differentials || []);
    return source.filter((c) => c !== ADHD_CONDITION_LABEL);
  }, [emerging]);

  const ssiColleagueNotes = emerging.ssiClinician;
  const leadColleagueNotes = emerging.leadClinician;
  const tocNotes = emerging.leadClinician;

  const ssiDisplayName =
    ssiColleagueNotes?.updatedByName
    || patientDataModel?.clinicians?.clinicianSSI?.clinicianName
    || 'SSI clinician';
  const leadDisplayName =
    leadColleagueNotes?.updatedByName
    || patientDataModel?.clinicians?.clinicianLead?.clinicianName
    || 'Lead clinician';

  // "Last updated" per column. Prefer per-clinician updatedAt; otherwise use
  // module completion proxies so the Lead still sees when notes were last set.
  const ssiLastUpdated = useMemo(() => {
    if (ssiColleagueNotes?.updatedAt) return ssiColleagueNotes.updatedAt;
    const reportTs = patientDataModel?.documents?.semistructuredinterviewReport?.lastGenerated;
    if (reportTs) return reportTs;
    return patientDataModel?.dataModel?.['3_semiStructuredInterview']?.firstCompletedAt || null;
  }, [patientDataModel, ssiColleagueNotes?.updatedAt]);

  const leadLastUpdated = useMemo(() => {
    if (leadColleagueNotes?.updatedAt) return leadColleagueNotes.updatedAt;
    return patientDataModel?.dataModel?.['2_clinicalInterview']?.firstCompletedAt || null;
  }, [patientDataModel, leadColleagueNotes?.updatedAt]);

  // TOC single-column notes (legacy / single-clinician path)
  const formulationNotes = tocNotes?.formulation || '';
  const confidentialNotes = tocNotes?.confidentialNotes || '';

  // ---------------------------------------------------------------------------
  // SSI symptom and impairment decisions. Same rows and the same pop-up
  // tables as Review & Synthesis, read from the SSI stage itself (not the
  // adult-shaped legacy copy, whose columns are "adulthood" / "childhood"
  // and whose headings say "day-to-day impact"). Hidden until the interview
  // has been started, so an empty table is not read as "nothing present".
  // ---------------------------------------------------------------------------
  const ssiStage = assessmentStages?.semiStructuredInterview;
  const ssiStarted = Boolean(ssiStage) && ssiStage.status !== 'notStarted';

  // Treatment contraindication screening (WLC, cypAdhdFlow.medicalScreening
  // === contraindicationsV1): the Yes/No decisions the interviewing
  // clinician recorded on the CI's Medical History and Family History steps,
  // read back here so the MDT sees them before deciding. Same "hidden until
  // the stage has been started" rule as the SSI summary above, so a CI that
  // was never opened does not read as ten clean Nos. wf comes from the
  // bridged patientDataModel, whose workflowConfig is the patient's stamp.
  const ciStage = assessmentStages?.clinicalInterview;
  const showMedicalScreening = Boolean(wf.cypShowMedicalScreening)
    && Boolean(ciStage)
    && ciStage.status !== 'notStarted';

  const decisionRows = useMemo(
    () => buildCypSsiDecisionRows(ssiStage?.data),
    [ssiStage?.data],
  );
  const summaryColumns = useMemo(
    () => cypSsiSummaryColumns({
      dateOfBirth: patientDataModel?.dateOfBirth,
      useOnsetPair: wf.cypUseOnsetPair,
    }),
    [patientDataModel?.dateOfBirth, wf.cypUseOnsetPair],
  );

  // MDT Review orgs (TOC, WLC) show "MDT Review"; others show "Review".
  const reviewPageTitle = (wf.isMDTReview || wf.isTOCWorkflow) ? 'MDT Review' : 'Review';

  // ---------------------------------------------------------------------------
  // DDSideNavbar — always currentSectionIndex=0 (Review is this page)
  // ---------------------------------------------------------------------------
  const ddNavSections = useMemo(
    () => buildDDNavSections({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      isTOCWorkflow: wf.isTOCWorkflow,
      isSLaMWorkflow: wf.isSLaMWorkflow,
      isMDTReview: wf.isMDTReview,
      isSembleMdtReportEnabled: false /* CYP: MDT Plan descoped (22 Aug) */,
      // CYP reads its own skip key (cypAdhdFlow.differentialsDecisions),
      // never the adult ddFlow one — same behaviour, independent toggle.
      skipDifferentialsDecisions: wf.cypSkipDifferentialsDecisions,
      currentSectionIndex: 0,
    }),
    [patientDataModel, wf.isFlatDifferentials, wf.isTOCWorkflow, wf.isSLaMWorkflow, wf.isMDTReview, wf.cypSkipDifferentialsDecisions]
  );

  // Must match buildDDNavSections' conditional Other Conditions step so
  // sidebar index 2 maps to Final Formulation when that step is omitted.
  const hasOtherConditions = useMemo(
    () => hasActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      skipDifferentialsDecisions: wf.cypSkipDifferentialsDecisions,
    }),
    [patientDataModel, wf.isFlatDifferentials, wf.cypSkipDifferentialsDecisions],
  );

  // Whether MDT Plan appears in the sidebar for this patient.
  const ddSection1 = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'];
  const hasMdtPlan = false /* CYP: MDT Plan descoped (22 Aug) */ && isAdhdPositiveDecision(ddSection1);

  // Navigate between DD pages from the sidebar.  MDT Review has no pending
  // writes to flush, so we navigate directly without a flush step.
  const navigateToSection = useCallback((index) => {
    navigate(getDDRouteForSectionIndex(patientId, index, hasOtherConditions, hasMdtPlan));
  }, [patientId, navigate, hasOtherConditions, hasMdtPlan]);

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Diagnostic Decision', null],
    [reviewPageTitle, null],
  ];

  if (!patientDataModel) return null;

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Outer flex row: DDSideNavbar (left) + page body (right) */}
      <div className="flex items-start">

        <DDSideNavbar
          sections={ddNavSections}
          currentSectionIndex={0}
          navigateToSection={navigateToSection}
        />

        <main className="flex-grow p-8">

          {/* Preview mode info banner — shown when opened via a Preview button */}
          {isPreviewMode && <PreviewModeBanner />}

          {/* Top bar: back button (left) + page title (centre) + next button (right) */}
          <div className="flex justify-between items-center mb-6">

            <div className="w-1/3 flex justify-start">
              <button
                onClick={() => navigate(`/patient/${patientId}/cyp-adhd`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                <span className="font-bold">Back to Patient Overview</span>
              </button>
            </div>

            <div className="w-1/3 flex justify-center">
              <h1 className="text-2xl font-bold">{reviewPageTitle}</h1>
            </div>

            <div className="w-1/3 flex justify-end">
              <button
                onClick={() => navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/adhd`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
              >
                <span>Next: Decision for ADHD</span>
                <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            </div>

          </div>

          {/* Behavioural Observation Appointment capture — renders only when
              the patient's workflow stamp asks for it (TOC). Sits first on
              the MDT Review by decision (23 Sept standup). Read-only for
              anyone who is not the assigned DD clinician, and for previews. */}
          <CypAdhdBoaCapturePanel canEdit={canEdit} />

          {/* ----------------------------------------------------------------
              Panel 1: Supporting Documents — the approved CYP CI and SSI
              reports. Shared component; hides when nothing is approved.
          ---------------------------------------------------------------- */}
          <CypAdhdSupportingDocumentsPanel
            patientId={patientId}
            clinicianId={auth.currentUser?.uid}
            documents={patientDataModel?.documents}
            reportContent={reportContent}
            className="mb-6"
          />

          {/* ----------------------------------------------------------------
              Panel 2: Rating Scales — the CYP SNAP-IV 26 panel (parent/carer
              and school columns), the same panel the overview shows. The
              adult PatientScalesPanel keys off the monolithic
              1_preAppointmentInformation model, which CYP does not have.
          ---------------------------------------------------------------- */}
          <div className="mb-6">
            <CypSnapScalesPanel
              guardianView={guardianView}
              schoolRepView={schoolRepView}
              onViewGuardianDetails={handleViewGuardianScale}
              onViewSchoolDetails={handleViewSchoolScale}
            />
          </div>

          {/* Same slide-over as the Patient Overview magnifying glass:
              the raw SNAP-IV questions and the informant's answers. */}
          <FormDetailsPanel
            isOpen={isScaleDetailOpen && !!scaleDetailForm}
            formInfo={scaleDetailForm}
            patientData={patientDataModel}
            informantData={informantData}
            onClose={() => setIsScaleDetailOpen(false)}
          />

          {/* ----------------------------------------------------------------
              TOC: Condition(s) Referred For + Emerging Differentials
              SLaM: skipped — SSI clinician does not record differentials here;
              the Lead records those on CI Review & Synthesis.
          ---------------------------------------------------------------- */}
          {!wf.hasCIReview && (
            <>
              <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
                <h3 className="font-bold text-lg mb-3">Condition(s) Referred For</h3>
                <div className="rounded-lg border border-gray-200 bg-gray-100 px-4 py-3">
                  <p className="text-base text-gray-700 flex items-start gap-2">
                    <span className="text-gray-400 mt-0.5 select-none">•</span>
                    <span className="font-semibold">{ADHD_CONDITION_LABEL}</span>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <ConditionSelectionPanelTOC
                  title="Emerging Differentials"
                  variant={wf.cypSkipDifferentialsDecisions ? 'non-report' : 'default'}
                  titleMeta={
                    isValidTimestamp(ssiLastUpdated) ? (
                      <span className="text-xs text-gray-400 italic">
                        Last updated {formatDateTime(ssiLastUpdated)}
                      </span>
                    ) : null
                  }
                  subtitle={
                    isValidTimestamp(ssiLastUpdated)
                      ? undefined
                      : 'Additional conditions under consideration from the SSI review.'
                  }
                  selectedOptions={differentials}
                  isConfirmed={true}
                  canEdit={false}
                />
              </div>
            </>
          )}

          {/* Same summary card as CYP Review & Synthesis: two buttons, each
              opening its table. Closed on arrival, so the tables are a check
              the MDT opens rather than the body of the page. */}
          {ssiStarted && (
            <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
              <h2 className="font-bold text-lg mb-4">
                Summary of Semi-Structured Interview Decisions
              </h2>
              <div className="flex gap-3">
                <CypAdhdDecisionSummaryDialog
                  label="View Symptoms Decisions"
                  title="Symptoms Summary"
                  description="DSM-5 symptom decisions, with persistence, age of onset and the number of settings each symptom is seen in"
                >
                  <CypAdhdSsiDecisionTables
                    show="symptoms"
                    inattention={decisionRows.inattention}
                    hyperactivity={decisionRows.hyperactivity}
                    onsetAsked={summaryColumns.onsetAsked}
                    afterTwelveAsked={summaryColumns.afterTwelveAsked}
                  />
                </CypAdhdDecisionSummaryDialog>
                <CypAdhdDecisionSummaryDialog
                  label="View Impairment Decisions"
                  title="Functional Impairment"
                  description="Whether each domain shows at least moderate impairment"
                >
                  <CypAdhdSsiDecisionTables
                    show="impairment"
                    domains={decisionRows.domains}
                  />
                </CypAdhdDecisionSummaryDialog>
              </div>
            </div>
          )}

          {/* Treatment contraindication screening read-out (WLC only).
              One panel for the Medical History items and the Family History
              item. Read-only: the answers are edited on the Clinical Interview,
              and this card reads the same stage field, so a later edit there
              shows up here. Amber, with the "not in the report" corner, so
              it is not mistaken for something the letter will draft. */}
          {showMedicalScreening && (
            <div className="mb-6" data-testid="mdt-medical-screening-card">
              <CypAdhdMedicalScreeningSummary
                groups={CYP_MEDICAL_SCREENING_GROUPS}
                data={ciStage?.medicalScreening}
                title="Cardiac and medical screening"
                sourceNote={CYP_MEDICAL_SCREENING_SOURCE_NOTE}
              />
            </div>
          )}

          {/* ----------------------------------------------------------------
              Emerging Formulation + Confidential Notes (read-only)
              SLaM: SSI + Lead side by side for comparison before deciding.
              TOC: single assessing-clinician panels (legacy paths).
          ---------------------------------------------------------------- */}
          <DualClinicianEmergingNotes
            showDual={wf.hasCIReview}
            ssiClinician={ssiColleagueNotes}
            leadClinician={leadColleagueNotes}
            ssiDisplayName={ssiDisplayName}
            leadDisplayName={leadDisplayName}
            ssiLastUpdated={ssiLastUpdated}
            leadLastUpdated={leadLastUpdated}
            showConfidential
            alwaysShowConfidential={wf.hasCIReview}
            singleFormulation={formulationNotes}
            singleConfidential={confidentialNotes}
            singleLastUpdated={ssiLastUpdated}
            singleVariant="amber"
          />

          {/* Bottom navigation — mirrors top bar for long-page convenience */}
          <div className="flex justify-between items-center mt-2">
            <div className="w-1/3 flex justify-start">
              <button
                onClick={() => navigate(`/patient/${patientId}/cyp-adhd`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                <span className="font-bold">Back to Patient Overview</span>
              </button>
            </div>

            <div className="w-1/3" />

            <div className="w-1/3 flex justify-end">
              <button
                onClick={() => navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/adhd`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
              >
                <span>Next: Decision for ADHD</span>
                <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            </div>
          </div>

        </main>
      </div>

    </Layout>
  );
}
